/**
 * identity-service client (dao-admin-suite) — the suite-login front-door wiring.
 *
 * Story: uid-001-suite-login-frontdoor (capstone — flips blog-admin 401→200).
 *
 * This module owns the three credentialed XHRs the admin suite makes to the
 * identity-service for the hosted-login-front-door flow:
 *
 *  1. {@link buildLoginFrontdoorUrl} — the URL the suite redirects an
 *     unauthenticated admin to. The hosted login UI authenticates the user
 *     (email+password, TOTP) against identity-service, then redirects back to
 *     `<return_origin>?code=<one-time-code>`.
 *  2. {@link exchangeCodeForJwt} — on the `?code=` return, POST `/token/exchange`
 *     `{code}` (credentialed) → `{jwt}`. The httpOnly `hwdao_refresh` cookie is set
 *     by identity-service on this same response (Domain=.helloworlddao.com) — JS
 *     never sees it.
 *  3. {@link refreshIdentityJwt} — silent refresh: POST `/token/refresh` with
 *     `credentials: 'include'` and NO body (the httpOnly cookie carries the refresh
 *     token) → fresh `{jwt}` + rotated cookie.
 *  4. {@link logoutIdentitySession} — POST `/logout` (credentialed) to clear the
 *     refresh cookie + revoke the token family.
 *
 * **Backward-compatible.** None of this forces existing cookie sessions to log
 * out. The suite keeps working on the legacy oracle-bridge cookie path;
 * it merely *also* obtains + presents an identity-service JWT once the front-door
 * flow runs. The cookie retirement is a SEPARATE later story.
 *
 * The identity-service base + the hosted-login-front-door base are env-driven
 * (`VITE_IDENTITY_SERVICE_URL`, `VITE_LOGIN_FRONTDOOR_URL`) so staging/prod differ
 * by config, not code.
 */

import { boundedRefresh, type RefreshOutcome } from '@hello-world-dao/auth';

/** Default identity-service base (staging) when the env var is unset. */
const DEFAULT_IDENTITY_SERVICE_URL = 'https://identity.staging.helloworlddao.com';

/** Default hosted-login-front-door base (staging) when the env var is unset. */
const DEFAULT_LOGIN_FRONTDOOR_URL = 'https://register.staging.helloworlddao.com';

/** The query parameter the hosted login UI reads for the cross-domain handoff. */
export const RETURN_ORIGIN_PARAM = 'return_origin';

/** The query parameter the front-door redirect carries the one-time code in. */
export const CODE_PARAM = 'code';

/** identity-service base URL, trailing slash stripped. */
export function getIdentityServiceUrl(): string {
  const raw = import.meta.env.VITE_IDENTITY_SERVICE_URL || DEFAULT_IDENTITY_SERVICE_URL;
  return raw.replace(/\/+$/, '');
}

/** Hosted-login-front-door base URL, trailing slash stripped. */
export function getLoginFrontdoorUrl(): string {
  const raw = import.meta.env.VITE_LOGIN_FRONTDOOR_URL || DEFAULT_LOGIN_FRONTDOOR_URL;
  return raw.replace(/\/+$/, '');
}

/**
 * This suite's own served origin — the `return_origin` the front-door redirects
 * back to after authentication. Using `window.location.origin` (rather than a
 * hard-coded env value) guarantees the origin is the exact host serving the page,
 * which is also what identity-service's `ALLOWED_RETURN_ORIGINS` + credentialed
 * CORS allowlist are keyed on.
 */
export function getSuiteOrigin(): string {
  return window.location.origin;
}

/**
 * Build the hosted-login-front-door URL to redirect an unauthenticated admin to.
 * The front-door authenticates the user, then redirects back to
 * `<return_origin>?code=<code>`.
 *
 * @returns e.g. `https://register.staging.helloworlddao.com/login?return_origin=https%3A%2F%2Fadmin.staging.helloworlddao.com`
 */
export function buildLoginFrontdoorUrl(): string {
  const url = new URL(`${getLoginFrontdoorUrl()}/login`);
  url.searchParams.set(RETURN_ORIGIN_PARAM, getSuiteOrigin());
  return url.toString();
}

/** Shape of the identity-service `/token/exchange` + `/token/refresh` success body. */
interface JwtResponse {
  jwt: string;
}

/**
 * Exchange a one-time front-door code for an identity-service access JWT.
 *
 * POSTs `/token/exchange {code}` CREDENTIALED (so identity-service can set the
 * httpOnly `hwdao_refresh` cookie on the response). Returns the JWT on success, or
 * `null` on any failure (invalid/used/expired code, network error) — callers fall
 * back to the existing cookie session rather than hard-breaking.
 */
export async function exchangeCodeForJwt(code: string): Promise<string | null> {
  try {
    const res = await fetch(`${getIdentityServiceUrl()}/token/exchange`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    });
    if (!res.ok) return null;
    const data = (await res.json()) as JwtResponse | null;
    return data?.jwt && data.jwt.length > 0 ? data.jwt : null;
  } catch {
    return null;
  }
}

/**
 * Silently refresh the identity-service access JWT using the httpOnly refresh
 * cookie.
 *
 * POSTs `/token/refresh` with `credentials: 'include'` and NO body — the cookie
 * carries the refresh token; identity-service rotates it (fresh cookie) and mints
 * a new access JWT. Returns the fresh JWT, or `null` when refresh fails (expired /
 * revoked / reused / no cookie) so the caller can clear the JWT and redirect to
 * login.
 */
export async function refreshIdentityJwt(): Promise<string | null> {
  try {
    const res = await fetch(`${getIdentityServiceUrl()}/token/refresh`, {
      method: 'POST',
      credentials: 'include',
    });
    if (!res.ok) return null;
    const data = (await res.json()) as JwtResponse | null;
    return data?.jwt && data.jwt.length > 0 ? data.jwt : null;
  } catch {
    return null;
  }
}

/**
 * Bounded, reuse-safe variant of {@link refreshIdentityJwt} for the pre-mount
 * session-rehydrate bootstrap (bl-977 / bl-985).
 *
 * Unlike `refreshIdentityJwt` (which collapses every failure to `null`, so a
 * transient blip is indistinguishable from a genuine logout and bounces a
 * valid-cookie admin to the sign-in shell), this returns a discriminated
 * {@link RefreshOutcome}:
 *   - `authenticated` — a 200 with a well-formed JWT.
 *   - `unauthenticated` — a definitive HTTP 401 (expired / revoked / reused / no
 *     cookie). ONLY this outcome should paint the login shell.
 *   - `transient-error` — timeout, 5xx/429, malformed 200, or a connection throw
 *     whose retries were exhausted. The caller MUST hold a loader (never login).
 *
 * The attempt is bounded by an `AbortController` timeout so a hung refresh can't
 * stall the load. bl-1248-1: a genuine 5xx is now retried AT MOST ONCE inside a
 * deadline ≤ the server grace window (safe because a post-500 either handed the
 * successor back as a Set-Cookie or lands in the grace window — see boundedRefresh);
 * a timeout / malformed-200 / 401 / 429 is never retried, and a connection-level
 * throw retries on its own bl-789-safe budget. Retries are strictly sequential
 * inside the caller's single-flight.
 *
 * bl-985: consumes the shared {@link boundedRefresh} primitive published in
 * `@hello-world-dao/auth@3.1.0` (the de-duplicated home of the per-suite pilot
 * copies) rather than a suite-local state machine.
 */
export async function refreshIdentityJwtBounded(): Promise<RefreshOutcome<string>> {
  return boundedRefresh<string>(
    (signal) =>
      fetch(`${getIdentityServiceUrl()}/token/refresh`, {
        method: 'POST',
        credentials: 'include',
        signal,
      }),
    async (res) => {
      const data = (await res.json()) as JwtResponse | null;
      return data?.jwt && data.jwt.length > 0 ? data.jwt : null;
    },
  );
}

/**
 * Revoke the identity-service refresh-token family + clear the httpOnly cookie.
 * Best-effort: a failure does not block the rest of the logout flow.
 */
export async function logoutIdentitySession(): Promise<void> {
  try {
    await fetch(`${getIdentityServiceUrl()}/logout`, {
      method: 'POST',
      credentials: 'include',
    });
  } catch {
    // Best-effort — the caller proceeds with the local clear + redirect.
  }
}
